import React from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import styles from "./ReportChart.module.scss";

interface ReportChartProps {
  title?: string;
  subtitle?: string;
  type: "line" | "bar" | "area" | "pie" | "donut" | "radialBar" | "scatter" | "heatmap" | "treemap";
  series: ApexOptions["series"];
  options?: ApexOptions;
  height?: number;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
}

const DEFAULT_COLORS = [
  "#4f46e5",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#0ea5e9",
  "#8b5cf6",
  "#ec4899",
];

const ReportChart: React.FC<ReportChartProps> = ({
  title,
  subtitle,
  type,
  series,
  options,
  height = 320,
  isLoading = false,
  isEmpty = false,
  emptyMessage,
}) => {
  const { t } = useTranslation();
  const resolvedEmptyMessage = emptyMessage ?? t("reportChart.noData");
  const mergedOptions: ApexOptions = {
    chart: {
      toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } },
      fontFamily: "inherit",
      ...options?.chart,
    },
    colors: options?.colors ?? DEFAULT_COLORS,
    dataLabels: { enabled: false, ...options?.dataLabels },
    grid: { borderColor: "#e2e8f0", strokeDashArray: 4, ...options?.grid },
    legend: { position: "bottom", ...options?.legend },
    ...options,
  };

  return (
    <div className={styles.wrap}>
      {(title || subtitle) && (
        <div className={styles.head}>
          {title && <h4 className={styles.title}>{title}</h4>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      )}
      <div className={styles.body} style={{ minHeight: height }}>
        {isLoading ? (
          <div className={styles.state}>
            <div className={styles.spinner} />
            <span>{t("loading")}</span>
          </div>
        ) : isEmpty ? (
          <div className={styles.state}>
            <span>{resolvedEmptyMessage}</span>
          </div>
        ) : (
          <Chart
            type={type}
            series={series ?? []}
            options={mergedOptions}
            height={height}
          />
        )}
      </div>
    </div>
  );
};

export default ReportChart;
